import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChoremeioFieldConfigItem } from '../configuration/components/choremeioFields.config';
import { validateAmka } from '@utils/amka-validation';

type ValidationRule = (value: string) => string | null;
type ValidationSchema = Record<string, ValidationRule | undefined>;

export const useDynamicValidation = (
  fieldConfigs: ChoremeioFieldConfigItem[]
): ValidationSchema => {
  const { t } = useTranslation(['create-patient-modal']);

  return useMemo(() => {
    const validations: ValidationSchema = {};

    fieldConfigs.forEach((field) => {
      switch (field.type) {
        case 'field':
          if (field.required) {
            validations[field.fieldName] = (value: string) => {
              if (!value) {
                return t(`errors.${field.fieldName}`, 'Error', {
                  ns: 'create-patient-modal',
                });
              }
              if (
                field.fieldType === 'email' &&
                !/^\S+@\S+\.\S+$/.test(value)
              ) {
                return t('errors.invalidEmail', { ns: 'create-patient-modal' });
              }
              if (
                field.fieldType === 'phone' &&
                !/\+?[0-9]{11,13}/.test(value)
              ) {
                return t('errors.invalidPhone', { ns: 'create-patient-modal' });
              }
              if (field.fieldType === 'amka' && !validateAmka(value)) {
                return t('errors.invalidAmka', { ns: 'create-patient-modal' });
              }
              return null;
            };
          } else {
            validations[field.fieldName] = (value: string) => {
              if (
                value &&
                field.fieldType === 'email' &&
                !/^\S+@\S+\.\S+$/.test(value)
              ) {
                return t('errors.invalidEmail', { ns: 'create-patient-modal' });
              }
              if (
                value &&
                field.fieldType === 'phone' &&
                !/\+?[0-9]{11,13}/.test(value)
              ) {
                return t('errors.invalidPhone', { ns: 'create-patient-modal' });
              }
              if (
                value &&
                field.fieldType === 'amka' &&
                !validateAmka(value)
              ) {
                return t('errors.invalidAmka', { ns: 'create-patient-modal' });
              }
              return null;
            };
          }
          break;

        case 'group':
          field.fields.forEach((subField) => {
            if (subField.required) {
              validations[subField.fieldName] = (value: string) => {
                if (!value) {
                  return t(`errors.${subField.fieldName}`, 'Error', {
                    ns: 'create-patient-modal',
                  });
                }

                if (
                  subField.fieldType === 'email' &&
                  !/^\S+@\S+\.\S+$/.test(value)
                ) {
                  return t('errors.invalidEmail', {
                    ns: 'create-patient-modal',
                  });
                }

                if (
                  subField.fieldType === 'phone' &&
                  !/\+?[0-9]{11,13}/.test(value)
                ) {
                  return t('errors.invalidPhone', {
                    ns: 'create-patient-modal',
                  });
                }
                if (
                  subField.fieldType === 'amka' &&
                  !validateAmka(value)
                ) {
                  return t('errors.invalidAmka', {
                    ns: 'create-patient-modal',
                  });
                }

                return null;
              };
            } else {
              validations[subField.fieldName] = (value: string) => {
                if (
                  value &&
                  subField.fieldType === 'email' &&
                  !/^\S+@\S+\.\S+$/.test(value)
                ) {
                  return t('errors.invalidEmail', {
                    ns: 'create-patient-modal',
                  });
                }
                if (
                  value &&
                  subField.fieldType === 'phone' &&
                  !/\+?[0-9]{11,13}/.test(value)
                ) {
                  return t('errors.invalidPhone', {
                    ns: 'create-patient-modal',
                  });
                }
                if (
                  value &&
                  subField.fieldType === 'amka' &&
                  !validateAmka(value)
                ) {
                  return t('errors.invalidAmka', {
                    ns: 'create-patient-modal',
                  });
                }
                return null;
              };
            }
          });
          break;

        case 'divider':
          break;
      }
    });

    return validations;
  }, [fieldConfigs, t]);
};
